import React from 'react';
import styled from 'styled-components';

/**
 * TaxonomyConfirmation Component
 * Displays top 3 taxonomy matches when confidence is between 0.70-0.85
 * Reuses confidence color logic from ResultsPanel.jsx
 */
const TaxonomyConfirmation = ({ alternatives, message, confidence, onSelect, onCancel }) => {
  const handleSelect = (alternative) => {
    onSelect(alternative);
  };

  return (
    <Container>
      <Header>
        <ConfidenceBar confidence={confidence}>
          <ConfidenceFill confidence={confidence} />
          <ConfidenceText>
            <ConfidenceLabel>Match Confidence:</ConfidenceLabel>
            <ConfidenceValue>{(confidence * 100).toFixed(1)}%</ConfidenceValue>
          </ConfidenceText>
        </ConfidenceBar>
        
        <MessageText>{message}</MessageText>
        <Subtitle>Please select the best match from the options below:</Subtitle>
      </Header>

      <AlternativesList>
        {alternatives.map((alt, index) => (
          <AlternativeCard key={index} onClick={() => handleSelect(alt)}>
            <CardHeader>
              <OptionNumber>Option {index + 1}</OptionNumber>
              <ConfidenceBadge confidence={alt.confidence}>
                {(alt.confidence * 100).toFixed(1)}%
              </ConfidenceBadge>
            </CardHeader>
            
            <TaxonomyPath>{alt.path}</TaxonomyPath>
            
            <HierarchyDetails>
              {alt.theme_category && (
                <DetailRow>
                  <Label>Category:</Label>
                  <Value>{alt.theme_category}</Value>
                </DetailRow>
              )}
              {alt.theme && (
                <DetailRow>
                  <Label>Theme:</Label>
                  <Value>{alt.theme}</Value>
                </DetailRow>
              )}
              {alt.sub_theme && (
                <DetailRow>
                  <Label>Sub-theme:</Label>
                  <Value>{alt.sub_theme}</Value>
                </DetailRow>
              )}
              {alt.theme_classification && (
                <DetailRow>
                  <Label>Classification:</Label>
                  <Value>{alt.theme_classification}</Value>
                </DetailRow>
              )}
            </HierarchyDetails>
          </AlternativeCard>
        ))}
      </AlternativesList>

      <Actions>
        <CancelButton onClick={onCancel}>
          Cancel
        </CancelButton>
        <HelpText>
          💡 Click on a card to select it, or cancel to rephrase your query
        </HelpText>
      </Actions>
    </Container>
  );
};

export default TaxonomyConfirmation;

// Styled Components
const Container = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin: 1rem 0;
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
`;

const ConfidenceBar = styled.div`
  position: relative;
  background: #f5f5f5;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const ConfidenceFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${(props) => props.confidence * 100}%;
  background: ${(props) =>
    props.confidence >= 0.85
      ? 'rgba(40, 167, 69, 0.2)'
      : props.confidence >= 0.70
      ? 'rgba(255, 193, 7, 0.2)'
      : 'rgba(220, 53, 69, 0.2)'};
  z-index: 0;
  transition: width 0.3s ease;
`;

const ConfidenceText = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ConfidenceLabel = styled.span`
  font-size: 0.9rem;
  color: #666;
  font-weight: 600;
`;

const ConfidenceValue = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: #333;
`;

const MessageText = styled.p`
  font-size: 1rem;
  color: #333;
  margin: 0 0 0.5rem 0;
  font-weight: 600;
`;

const Subtitle = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin: 0;
`;

const AlternativesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const AlternativeCard = styled.div`
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e7f3ff;
    border-color: #4a90e2;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(74, 144, 226, 0.2);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const OptionNumber = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: #4a90e2;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ConfidenceBadge = styled.span`
  background: ${(props) =>
    props.confidence >= 0.85
      ? 'rgba(40, 167, 69, 0.15)'
      : props.confidence >= 0.70
      ? 'rgba(255, 193, 7, 0.15)'
      : 'rgba(220, 53, 69, 0.15)'};
  color: ${(props) =>
    props.confidence >= 0.85
      ? '#28a745'
      : props.confidence >= 0.70
      ? '#ffc107'
      : '#dc3545'};
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 700;
`;

const TaxonomyPath = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.75rem;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  border-left: 3px solid #4a90e2;
`;

const HierarchyDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 0.5rem;
  font-size: 0.9rem;
`;

const Label = styled.span`
  color: #666;
  font-weight: 600;
  min-width: 100px;
`;

const Value = styled.span`
  color: #333;
  font-family: 'Courier New', monospace;
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
`;

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #5a6268;
  }
`;

const HelpText = styled.p`
  font-size: 0.85rem;
  color: #666;
  margin: 0;
  font-style: italic;
`;
